/**
 * Telemetry — emits small, structured, PII-free events describing chat turn
 * outcomes to the server, which is responsible for turning them into
 * OpenTelemetry traces/metrics (see ADR-0002). Fire-and-forget: telemetry
 * must never affect the add-in's behavior, so failures are swallowed.
 *
 * Events never carry cell values, sheet contents, or free-text prompt
 * content — only action types, outcomes, timings, and opaque ids.
 */

import type {
  ActionOutcome,
  ConfirmationRecord,
  ConfirmationSource,
  ActionErrorCode,
} from "./actionExecutor";
// Type-only, so this doesn't close a runtime import cycle with chatTurn.
import type {StopReason} from "./chatTurn";
import type {ErrorKind} from "./errorKind";
import {classifyError} from "./errorKind";
import type {PhaseDurations} from "./turnPhases";
import type {InvalidActionTelemetry} from "./actionParser";
import {getRuntimeConfig} from "./runtimeConfig";

const SESSION_ID_KEY = "telemetry_session_id";
const INSTALL_RECORDED_KEY = "telemetry_install_recorded";
const LAST_ACTIVE_DATE_KEY = "telemetry_last_active_date";

/** Deployment environment attached to every turn without carrying PII. */
export function getAddonEnv(): string {
  return getRuntimeConfig().ADDON_ENV ?? process.env.ADDON_ENV ?? "other";
}

let idCounter = 0;

/**
 * These ids only gate client-side de-dup and correlate telemetry — never a
 * security context — but CodeQL flags `Math.random()` wherever it appears
 * regardless, so `crypto.getRandomValues` is used instead everywhere it's
 * available, falling back to a counter (not `Math.random()`) only when no
 * Web Crypto API exists at all.
 */
function randomId(): string {
  if (typeof crypto !== "undefined") {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
    if (typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  }
  idCounter += 1;
  return `${Date.now().toString(36)}-${idCounter.toString(36)}`;
}

/** Random id for this browser session, persisted so it's stable across turns. */
export function getSessionId(): string {
  try {
    const existing = localStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const created = randomId();
    localStorage.setItem(SESSION_ID_KEY, created);
    return created;
  } catch {
    return randomId();
  }
}

/** Fresh id for one `runChatTurn` call, ties its events into one trace. */
export function generateTurnId(): string {
  return randomId();
}

/** Today's date in UTC, as YYYY-MM-DD — the boundary a day of activity is counted by. */
function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Short code a user can quote to support to find their turn's trace. */
export function errorReference(turnId: string): string {
  return turnId.replace(/-/g, "").slice(0, 8);
}

export interface TurnStartedEvent {
  type: "turn_started";
  turnId: string;
  sessionId: string;
  agentId?: string;
  addonEnv?: string;
  maxIterations?: number;
}

export interface ActionResultEvent {
  type: "action_result";
  turnId: string;
  sessionId: string;
  iteration: number;
  actionType: string;
  outcome: ActionOutcome;
  errorMessage?: string;
  errorCode?: ActionErrorCode;
}

export interface InvalidActionOutputEvent {
  type: "invalid_action_output";
  turnId: string;
  sessionId: string;
  iteration: number;
  reason: InvalidActionTelemetry["reason"];
  actionType?: string;
}

/**
 * One confirmation prompt shown to the user, and what they did with it.
 *
 * Kept separate from `action_result` because the two answer different
 * questions: an accepted confirmation still counts as a confirmation even
 * when the script it guarded then fails, and the accept/decline split is only
 * readable against the prompts that were actually shown.
 */
export interface ConfirmationResultEvent {
  type: "confirmation_result";
  turnId: string;
  sessionId: string;
  iteration: number;
  actionType: string;
  source: ConfirmationSource;
  outcome: ConfirmationRecord["outcome"];
}

export type TurnDegradationKind =
  | "context_truncated"
  | "context_read_failure"
  | "excel_settings_read_failure"
  | "history_truncated";

export interface TurnDegradationEvent {
  type: "turn_degradation";
  turnId: string;
  sessionId: string;
  kind: TurnDegradationKind;
  originalSize?: number;
  retainedSize?: number;
}

export interface TurnFinishedEvent {
  type: "turn_finished";
  turnId: string;
  sessionId: string;
  agentId?: string;
  addonEnv?: string;
  maxIterations?: number;
  /**
   * Typed rather than a bare string because this becomes the `stop_reason`
   * label on `chat_turn_outcomes_total`, and ADR-0002's first label rule only
   * permits it there while the set stays bounded. A free string would let a
   * typo open a new series.
   */
  stopReason: StopReason;
  iterations: number;
  durationMs: number;
  /**
   * Where the turn's time went, in milliseconds per phase, summed across
   * iterations. Rides on this event rather than arriving as per-phase events
   * of its own: a turn at the iteration cap passes through ~25 phases, and
   * posting each one mid-stream would multiply the add-in's request count for
   * exactly the slowest turns — the ones already struggling (issue #50).
   *
   * A total, not a breakdown by iteration: "was the model slow or was reading
   * the workbook slow" is a question about the turn, and per-iteration detail
   * belongs on the span, where the action children already carry it.
   */
  phaseDurationsMs: PhaseDurations;
  /**
   * Milliseconds a request spent in flight before the turn's first streamed
   * chunk arrived. Separate from `thinking` because the two move apart for
   * different reasons: a rising time to first chunk against flat generation
   * time is LibreChat queueing, not a slower model, and the totals alone
   * cannot tell those apart.
   *
   * One sample per turn, not an average over its passes — this stands in for
   * the wait the user sits through before anything appears on screen. That is
   * normally the first request; a turn whose opening pass streamed nothing at
   * all times the pass that did, which is still the first thing the user saw.
   * Unset when the turn ended before a single chunk arrived.
   */
  timeToFirstChunkMs?: number;
  /**
   * Why it failed, as a bounded enum safe to use as a metric label. Set only
   * when `stopReason` is `"failed"` — a kind on any other outcome would be a
   * time series that means nothing.
   */
  errorKind?: ErrorKind;
  /**
   * The failure's own message, for the Tempo span. Never a label: unbounded
   * text is what spans are for. Its sources are kept narrow so it stays free
   * of workbook and prompt content — an HTTP status line, a fault's type
   * name, or an Office.js error — which is why `librechatApi` reports a
   * malformed response by name rather than quoting what it was parsing.
   */
  errorMessage?: string;
}

/** Fires once ever per browser profile — the new-user growth curve (issue #56). */
export interface AddinInstalledEvent {
  type: "addin_installed";
  sessionId: string;
}

/** Fires at most once per UTC day per browser profile — sums to real DAU (issue #56). */
export interface AddinActiveDayEvent {
  type: "addin_active_day";
  sessionId: string;
}

/** User tried to send before configuring key/agent and was redirected to settings. */
export interface SendBlockedUnconfiguredEvent {
  type: "send_blocked_unconfigured";
  sessionId: string;
}

/** Outcome of the settings "Save & Test Connection" call. */
export interface SettingsTestEvent {
  type: "settings_test";
  sessionId: string;
  outcome: "success" | "failure" | "no_agents";
  /** Bounded failure kind, only when outcome is failure. */
  reason?: ErrorKind;
}

/**
 * How many exchanges deep a turn falls within its thread — one histogram
 * sample per turn, so a distribution answers "do people ask one question and
 * leave, or hold a real conversation" (issue #58).
 */
export interface ConversationDepthEvent {
  type: "conversation_depth";
  turnId: string;
  sessionId: string;
  turnIndex: number;
}

/**
 * The workbook's coarse size at the moment a turn's context was read. Counts
 * and a byte size, never sheet names, workbook names, cell values or
 * formulas — see `WorkbookShape` in `excelContext.ts` (issue #58).
 */
export interface WorkbookShapeEvent {
  type: "workbook_shape";
  turnId: string;
  sessionId: string;
  sheetCount: number;
  cellCount: number;
  snapshotBytes: number;
}

/**
 * How long a turn had been waiting when the user gave up on it — the
 * empirical answer to "how long will people wait before aborting" (issue
 * #58). Fired from the Stop button, not from `runChatTurn`: the loop reports
 * `stopReason: "stopped"` regardless of how long the wait was, and this is
 * the elapsed time the waiting bubble was already tracking and discarding.
 */
export interface TurnAbandonedEvent {
  type: "turn_abandoned";
  sessionId: string;
  elapsedMs: number;
}

export type TelemetryEvent =
  | TurnStartedEvent
  | ActionResultEvent
  | InvalidActionOutputEvent
  | ConfirmationResultEvent
  | TurnDegradationEvent
  | TurnFinishedEvent
  | AddinInstalledEvent
  | AddinActiveDayEvent
  | SendBlockedUnconfiguredEvent
  | SettingsTestEvent
  | ConversationDepthEvent
  | WorkbookShapeEvent
  | TurnAbandonedEvent;

export type StorageTelemetryEvent =
  | {type: "thread_evicted"; reason: "quota" | "max_threads"}
  | {
      type: "thread_save_failure";
      reason: "quota" | "quota_retry_failed" | "storage_error";
    }
  | {
      type: "thread_store_corrupt";
      reason: "invalid_json" | "invalid_shape" | "read_failure";
    };

export function recordTelemetryEvent(
  event: TelemetryEvent | StorageTelemetryEvent,
): void {
  fetch("/__telemetry", {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify(event),
  }).catch(() => {
    // Silently ignore — don't break the add-in if telemetry fails
  });
}

/**
 * Emits `addin_installed` exactly once per browser profile, guarded by a
 * localStorage flag so a restart or reload never re-fires it. Skips emitting
 * — rather than risk double-counting — when storage can't be read or
 * written at all; a cleared profile is a known, accepted re-count (issue #56),
 * not a bug this guards against.
 */
export function recordInstallEvent(): void {
  try {
    if (localStorage.getItem(INSTALL_RECORDED_KEY)) return;
    localStorage.setItem(INSTALL_RECORDED_KEY, "1");
  } catch {
    return;
  }
  recordTelemetryEvent({type: "addin_installed", sessionId: getSessionId()});
}

/**
 * Emits `addin_active_day` at most once per UTC day per browser profile —
 * the client-side de-dup that makes `addin_active_days_total` a real DAU
 * count, immune to pod restarts and replica count (issue #56). Skips
 * emitting when storage isn't usable, for the same reason as
 * `recordInstallEvent`.
 */
export function recordActiveDayEvent(): void {
  const today = todayUtc();
  try {
    if (localStorage.getItem(LAST_ACTIVE_DATE_KEY) === today) return;
    localStorage.setItem(LAST_ACTIVE_DATE_KEY, today);
  } catch {
    return;
  }
  recordTelemetryEvent({type: "addin_active_day", sessionId: getSessionId()});
}

/** Both adoption events, called once at startup — each guarded independently. */
export function recordAdoptionEvents(): void {
  recordInstallEvent();
  recordActiveDayEvent();
}

/** Counts a send attempt blocked before any turn can start (issue #57). */
export function recordSendBlockedUnconfiguredEvent(): void {
  recordTelemetryEvent({
    type: "send_blocked_unconfigured",
    sessionId: getSessionId(),
  });
}

/**
 * Records how long a turn had run when the user hit Stop — the elapsed-at-
 * abort measurement ADR-0002's amendment describes (issue #58).
 */
export function recordTurnAbandonedEvent(elapsedMs: number): void {
  recordTelemetryEvent({
    type: "turn_abandoned",
    sessionId: getSessionId(),
    elapsedMs,
  });
}

/** Counts one Save & Test Connection result from the Settings view (issue #57). */
export function recordSettingsTestEvent(
  outcome: "success" | "failure" | "no_agents",
  reason?: ErrorKind,
): void {
  recordTelemetryEvent({
    type: "settings_test",
    sessionId: getSessionId(),
    outcome,
    ...(outcome === "failure" && reason ? {reason} : {}),
  });
}

/** Failure helper that classifies the error into a bounded reason. */
export function recordSettingsTestFailure(err: unknown): void {
  recordSettingsTestEvent("failure", classifyError(err));
}
